import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
    buildDraftItems,
    commitDraftChanges,
    nextPendingId,
    revokePending,
} from './use-attachment-draft'
import type { DraftItem, PendingFile } from './use-attachment-draft'
import type { AttachmentsFieldDraft } from './grower-attachments-field'
import { growerKeys, useGrower } from '@/hooks/use-growers-query'

/** Cặp (vùng trong form ↔ id vùng thật) dùng để đẩy tệp lên sau khi lưu hộ. */
export type AreaDraftTarget = { draftKey: string; areaId?: string }

/** Kết quả một lượt commit — `missingArea` là số vùng không tìm ra id. */
export type AreaDraftCommitResult = { missingArea: number }

type FilesByKey = Record<string, PendingFile[]>
type IdsByKey = Record<string, string[]>

/**
 * Nháp giấy tờ đất cho TẤT CẢ vùng trồng của một hộ, gom trong MỘT hook.
 *
 * Vì sao không dùng `useAttachmentDraft` cho mỗi vùng: số vùng thay đổi theo
 * thao tác người dùng (thêm/xoá tab), mà gọi hook trong vòng lặp là vi phạm
 * rules-of-hooks. Ở đây state là hai map, còn `getDraft` chỉ dựng object thuần
 * cho từng vùng nên gọi bao nhiêu lần cũng được.
 *
 * Khoá là `draftKey` chứ KHÔNG phải id vùng: vùng vừa thêm chưa có id (server
 * mới cấp lúc lưu). `draftKey` sống trong chính giá trị form nên không lệch khi
 * người dùng xoá một vùng ở giữa — lấy index làm khoá thì tệp của vùng 3 nhảy
 * sang vùng 2.
 *
 * Vẫn giữ nếp "bấm Lưu mới áp dụng" như ô giấy tờ hồ sơ: chọn tệp ở vùng chưa
 * lưu vẫn được, `commitAll` chạy sau khi lưu hộ với id vùng lấy từ response.
 */
export function useAreaLandDrafts(growerId?: string) {
    const qc = useQueryClient()
    const { data: detail } = useGrower(growerId)
    const [pending, setPending] = useState<FilesByKey>({})
    const [removed, setRemoved] = useState<IdsByKey>({})

    // Ref soi gương state: commit chạy SAU khi hộ lưu xong, lúc đó query chi
    // tiết đã invalidate và component có thể đã render lại — đọc qua ref thì
    // vẫn đúng những gì người dùng đã chọn trước khi bấm Lưu.
    const pendingRef = useRef<FilesByKey>({})
    pendingRef.current = pending
    const removedRef = useRef<IdsByKey>({})
    removedRef.current = removed

    useEffect(() => {
        return () => {
            Object.values(pendingRef.current).forEach(revokePending)
        }
    }, [])

    const addFiles = useCallback((key: string, files: FileList | File[]) => {
        const arr = Array.from(files)
        if (arr.length === 0) return
        setPending((prev) => {
            const cur = prev[key] ?? []
            return {
                ...prev,
                [key]: [
                    ...cur,
                    ...arr.map((file) => ({
                        tempId: nextPendingId(file),
                        file,
                        url: URL.createObjectURL(file),
                    })),
                ],
            }
        })
    }, [])

    const removeItem = useCallback((key: string, item: DraftItem) => {
        if (item.kind === 'existing' && item.fileId) {
            const id = item.fileId
            setRemoved((prev) => {
                const cur = prev[key] ?? []
                return cur.includes(id)
                    ? prev
                    : { ...prev, [key]: [...cur, id] }
            })
            return
        }
        setPending((prev) => {
            const cur = prev[key] ?? []
            const found = cur.find((p) => `pd-${p.tempId}` === item.key)
            if (found) URL.revokeObjectURL(found.url)
            return {
                ...prev,
                [key]: cur.filter((p) => `pd-${p.tempId}` !== item.key),
            }
        })
    }, [])

    /**
     * Ô đính kèm của một vùng. Trả object thuần (không phải hook) nên gọi được
     * trong render của bất kỳ tab nào.
     *
     * `areaId` rỗng = vùng chưa lưu: chưa có tệp nào trên server để hiện, chỉ có
     * tệp vừa chọn nằm chờ.
     */
    const getDraft = useCallback(
        (draftKey: string, areaId?: string): AttachmentsFieldDraft => ({
            items: buildDraftItems(
                areaId ? detail?.files : [],
                'land',
                areaId,
                removed[draftKey] ?? [],
                pending[draftKey] ?? [],
            ),
            addFiles: (files) => addFiles(draftKey, files),
            remove: (item) => removeItem(draftKey, item),
        }),
        [detail?.files, pending, removed, addFiles, removeItem],
    )

    /**
     * Quên nháp của một vùng vừa bị xoá khỏi hộ. Không gọi thì tệp đang chờ của
     * vùng đã biến mất vẫn được tải lên ở lần Lưu kế tiếp.
     */
    const dropDraft = useCallback((draftKey: string) => {
        setPending((prev) => {
            if (!prev[draftKey]) return prev
            revokePending(prev[draftKey])
            const { [draftKey]: _dropped, ...rest } = prev
            return rest
        })
        setRemoved((prev) => {
            if (!prev[draftKey]) return prev
            const { [draftKey]: _dropped, ...rest } = prev
            return rest
        })
    }, [])

    const hasChanges =
        Object.values(pending).some((v) => v.length > 0) ||
        Object.values(removed).some((v) => v.length > 0)

    /**
     * Đẩy nháp của mọi vùng lên sau khi hộ đã lưu. `targets` ghép draftKey với
     * id vùng thật lấy từ response lưu hộ — vùng vừa tạo tới đây mới có id.
     *
     * Vùng có tệp chờ mà không tra ra id thì KHÔNG im lặng bỏ qua: đếm vào
     * `missingArea` để màn gọi báo cho người dùng biết tệp chưa lên.
     */
    const commitAll = useCallback(
        async (
            gid: string,
            targets: AreaDraftTarget[],
        ): Promise<AreaDraftCommitResult> => {
            let missingArea = 0
            if (!gid) return { missingArea }
            const done = new Set<string>()
            for (const { draftKey, areaId } of targets) {
                const filesToAdd = pendingRef.current[draftKey] ?? []
                const idsToDrop = removedRef.current[draftKey] ?? []
                if (filesToAdd.length === 0 && idsToDrop.length === 0) continue
                if (!areaId) {
                    missingArea += 1
                    continue
                }
                await commitDraftChanges({
                    growerId: gid,
                    kind: 'land',
                    areaId,
                    removedIds: idsToDrop,
                    pending: filesToAdd,
                })
                done.add(draftKey)
            }
            // Chỉ dọn đúng những vùng vừa đẩy lên. Xoá sạch cả map là vứt im
            // lặng tệp của vùng không nằm trong đợt này — xảy ra khi form bị nạp
            // lại giữa chừng (người khác sửa cùng hộ) làm khoá nháp đổi.
            if (done.size > 0) {
                setPending((prev) => omitKeys(prev, done))
                setRemoved((prev) => omitKeys(prev, done))
            }
            await qc.invalidateQueries({ queryKey: growerKeys.detail(gid) })
            await qc.invalidateQueries({ queryKey: growerKeys.lists() })
            return { missingArea }
        },
        [qc],
    )

    return { getDraft, dropDraft, commitAll, hasChanges }
}

/** Bỏ các khoá đã xử lý khỏi map, giữ nguyên tham chiếu cũ nếu không có gì để bỏ. */
function omitKeys<T>(
    map: Record<string, T>,
    keys: Set<string>,
): Record<string, T> {
    const out: Record<string, T> = {}
    for (const [k, v] of Object.entries(map)) {
        if (!keys.has(k)) out[k] = v
    }
    return out
}

export type AreaLandDrafts = ReturnType<typeof useAreaLandDrafts>
